import React, { useState, useEffect } from 'react';

// ─── Color Palette (From Real Software) ───────────────────────
const COLORS = {
    rawMaterial: { bg: '#E3F2FD', border: '#1976D2', text: '#0D47A1', accent: '#1976D2' },
    product: { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20', accent: '#2E7D32' },
    dispatch: { bg: '#FFF3E0', border: '#EF6C00', text: '#E65100', accent: '#EF6C00' },
    waste: { bg: '#FFEBEE', border: '#D32F2F', text: '#C62828', accent: '#D32F2F' },
    stock: { bg: '#F3E5F5', border: '#7B1FA2', text: '#4A148C', accent: '#7B1FA2' },
    line: '#CBD5E1',
    lineActive: '#1976D2',
};

// ─── Icons ──────────────────────────────────────────────────
const RawIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="7" width="20" height="10" rx="2" />
        <path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2" />
    </svg>
);

const ProductIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
    </svg>
);

const DispatchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="3" width="15" height="13" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
);

const WasteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 6h18m-2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2m-6 5l4 4m0-4l-4 4" />
    </svg>
);

const StockIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path d="M9 22V12h6v10" />
    </svg>
);

// ─── Diagram Data ──────────────────────────────────────────
const flowScenarios = {
    linear: {
        title: 'Simple Overview',
        nodes: [
            { id: 'r1', name: 'Raw Materials', x: 250, y: 50, type: 'raw' },
            { id: 'p1', name: 'Production Batch', x: 250, y: 180, type: 'product' },
            { id: 'd1', name: 'Market Dispatch', x: 250, y: 310, type: 'dispatch' },
        ],
        lines: [
            { id: 'l1', x1: 250, y1: 85, x2: 250, y2: 180 },
            { id: 'l2', x1: 250, y1: 215, x2: 250, y2: 310 },
        ]
    },
    branching: {
        title: 'One-to-Many Usage',
        nodes: [
            { id: 'br1', name: 'Hydrogen Tank #A', x: 250, y: 50, type: 'raw' },
            { id: 'bp1', name: 'PVA Solution', x: 100, y: 180, type: 'product' },
            { id: 'bp2', name: 'Industrial Glue', x: 250, y: 180, type: 'product' },
            { id: 'bp3', name: 'Cleaning Solvent', x: 400, y: 180, type: 'product' },
        ],
        lines: [
            { id: 'bl1', x1: 250, y1: 85, x2: 100, y2: 180 },
            { id: 'bl2', x1: 250, y1: 85, x2: 250, y2: 180 },
            { id: 'bl3', x1: 250, y1: 85, x2: 400, y2: 180 },
        ]
    },
    merging: {
        title: 'Many-to-One & Split Output',
        nodes: [
            { id: 'mi1', name: 'Hydrogen', x: 100, y: 50, type: 'raw' },
            { id: 'mi2', name: 'Purified Water', x: 250, y: 50, type: 'raw' },
            { id: 'mi3', name: 'Stabilizers', x: 400, y: 50, type: 'raw' },
            { id: 'mp1', name: 'High-Grade Batch #7', x: 250, y: 180, type: 'product' },
            { id: 'mo1', name: 'Market Dispatch', x: 100, y: 310, type: 'dispatch' },
            { id: 'mo2', name: 'Warehouse Stock', x: 250, y: 310, type: 'stock' },
            { id: 'mo3', name: 'Process Waste', x: 400, y: 310, type: 'waste' },
        ],
        lines: [
            { id: 'ml1', x1: 100, y1: 85, x2: 250, y2: 180 },
            { id: 'ml2', x1: 250, y1: 85, x2: 250, y2: 180 },
            { id: 'ml3', x1: 400, y1: 85, x2: 250, y2: 180 },
            { id: 'ml4', x1: 250, y1: 215, x2: 100, y2: 310 },
            { id: 'ml5', x1: 250, y1: 215, x2: 250, y2: 310 },
            { id: 'ml6', x1: 250, y1: 215, x2: 400, y2: 310 },
        ]
    }
};

const FlowNode = ({ item, isHovered, onHover, onLeave, connectedLines }) => {
    const colors = COLORS[item.type] || COLORS.product;
    const Icon = item.type === 'raw' ? RawIcon :
        item.type === 'dispatch' ? DispatchIcon :
            item.type === 'waste' ? WasteIcon :
                item.type === 'stock' ? StockIcon : ProductIcon;

    return (
        <g
            transform={`translate(${item.x - 70}, ${item.y - 35})`}
            className="transition-all duration-500 ease-out cursor-pointer group"
            onMouseEnter={() => onHover(item.id)}
            onMouseLeave={onLeave}
        >
            {/* Glow effect on hover */}
            {isHovered && (
                <rect
                    x="-5" y="-5" width="150" height="80" rx="14"
                    fill={colors.accent} className="opacity-20 blur-md animate-pulse"
                />
            )}
            <rect
                width="140" height="70" rx="12"
                fill={colors.bg}
                stroke={isHovered ? colors.accent : colors.border}
                strokeWidth={isHovered ? "2.5" : "1.5"}
                className={`transition-all duration-300 shadow-sm ${isHovered ? 'shadow-lg -translate-y-1' : ''}`}
            />
            <foreignObject width="140" height="70" className="pointer-events-none">
                <div className="flex flex-col items-center justify-center h-full text-center px-1">
                    <div className={`mb-1 transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`} style={{ color: colors.accent }}>
                        <Icon />
                    </div>
                    <div className="text-[10px] font-bold leading-tight" style={{ color: colors.text }}>{item.name}</div>
                    {item.type === 'product' && (
                        <div className={`text-[8px] px-2 py-0.5 rounded-full mt-1 transition-colors duration-300 ${isHovered ? 'bg-sky-500 text-white' : 'bg-green-600/80 text-white'}`}>
                            {isHovered ? 'PROCESSED' : 'VERIFIED'}
                        </div>
                    )}
                </div>
            </foreignObject>
        </g>
    );
};

const FlowLine = ({ x1, y1, x2, y2, active, isHighlighted }) => {
    const midY = (y1 + y2) / 2;
    const d = `M${x1},${y1} C${x1},${midY} ${x2},${midY} ${x2},${y2}`;

    return (
        <g>
            {/* Background Path */}
            <path d={d} fill="none" stroke={COLORS.line} strokeWidth="1.5" strokeDasharray="4,2" className="opacity-20" />

            {/* Primary Animated Line */}
            <path
                d={d} fill="none"
                stroke={isHighlighted ? 'url(#lineGradient)' : COLORS.lineActive}
                strokeWidth={isHighlighted ? "3" : "2"}
                strokeDasharray="1000" strokeDashoffset={active ? "0" : "1000"}
                className="transition-all duration-1000 ease-in-out opacity-60"
            />

            {/* Moving Particle */}
            {active && (
                <circle r="3" fill={isHighlighted ? '#0ea5e9' : '#1976D2'} className="shadow-lg">
                    <animateMotion dur="3s" repeatCount="indefinite" path={d} />
                </circle>
            )}
        </g>
    );
};

const SoftwareFlowDiagram = () => {
    const [scenario, setScenario] = useState('linear');
    const [hoveredNode, setHoveredNode] = useState(null);
    const [paused, setPaused] = useState(false);
    const modes = ['linear', 'branching', 'merging'];

    useEffect(() => {
        if (paused) return;
        const timer = setInterval(() => {
            setScenario(prev => {
                const idx = modes.indexOf(prev);
                return modes[(idx + 1) % modes.length];
            });
        }, 5000);
        return () => clearInterval(timer);
    }, [paused]);

    const activeData = flowScenarios[scenario];

    // Determine if a line should be highlighted based on hovered node
    const isLineHighlighted = (line) => {
        if (!hoveredNode) return false;
        // In this simple model, we check if the line's start or end coordinates closely match the logic
        // For a more robust version, we'd store node connections in the data
        const node = activeData.nodes.find(n => n.id === hoveredNode);
        if (!node) return false;

        // Match line start or end to node center
        const isStart = Math.abs(line.x1 - node.x) < 5 && Math.abs(line.y1 - (node.y + 35)) < 5;
        const isEnd = Math.abs(line.x2 - node.x) < 5 && Math.abs(line.y2 - (node.y - 35)) < 5;
        return isStart || isEnd;
    };

    return (
        <div className="bg-transparent overflow-visible flex flex-col items-center w-full group/diagram">
            <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between w-full max-w-[500px] px-4 gap-4">
                <div className="flex flex-col">
                    <div className="text-[10px] font-black tracking-widest text-slate-400 uppercase mb-1">
                        Supply Chain Scenario
                    </div>
                    <div className="text-sm font-bold text-slate-900 animate-fade-in" key={activeData.title}>
                        {activeData.title}
                    </div>
                </div>

                <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-full border border-slate-200 shadow-inner w-full sm:w-auto overflow-x-auto no-scrollbar">
                    {modes.map(m => (
                        <button
                            key={m}
                            onClick={() => { setScenario(m); setPaused(true); }}
                            className={`whitespace-nowrap px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider transition-all duration-300 ${scenario === m ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            {m === 'linear' ? 'Basic' : m === 'branching' ? 'Usage' : 'Global'}
                        </button>
                    ))}
                    <button
                        onClick={() => setPaused(!paused)}
                        className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${paused ? 'text-sky-500 bg-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        {paused ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        ) : (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                        )}
                    </button>
                </div>
            </div>

            <div className="relative w-full max-w-[500px] px-4">
                <svg
                    viewBox="10 10 480 380"
                    className="w-full h-auto overflow-visible bg-white/70 backdrop-blur-md rounded-[2rem] sm:rounded-[2.5rem] border border-slate-200 shadow-2xl transition-all duration-500"
                >
                    <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#4f46e5" />
                            <stop offset="100%" stopColor="#0ea5e9" />
                        </linearGradient>
                    </defs>

                    <g className="animate-fade-in" key={scenario}>
                        {activeData.lines.map(line => (
                            <FlowLine
                                key={line.id}
                                {...line}
                                active={true}
                                isHighlighted={isLineHighlighted(line)}
                            />
                        ))}
                        {activeData.nodes.map(node => (
                            <FlowNode
                                key={node.id}
                                item={node}
                                isHovered={hoveredNode === node.id}
                                onHover={setHoveredNode}
                                onLeave={() => setHoveredNode(null)}
                            />
                        ))}
                    </g>
                </svg>

            </div>

            <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-indigo-50 border border-indigo-200" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Input</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-200" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Production</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-orange-50 border border-orange-200" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase">Output</span>
                </div>
            </div>
        </div>
    );
};

export default SoftwareFlowDiagram;
